import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { ChatHistoryType } from '@/types/converse/chatHistory';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import React from 'react';

// Define the transcript message type
export interface TranscriptMessage {
  id: string;
  speaker: 'user' | 'agent';
  text: string;
  timestamp: string; // ISO date string
}

interface CallTranscriptProps {
  callId: string | null;
  transcript: ChatHistoryType['messages'];
  loading?: boolean;
}

function CallTranscript({
  callId,
  transcript,
  loading = false,
}: CallTranscriptProps): React.ReactNode {
  // Format timestamp to a readable format
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const { currentAlchemyst } = useAlchemystStoreForAi((store) => ({
    currentAlchemyst: store.alchemyst,
  }));

  if (!callId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <Typography variant="body1" color="textSecondary">
          Select a call to view its transcript
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Call Transcript
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {(transcript ?? []).length > 0 ? (
        (transcript ?? []).map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              mb: 2,
              flexDirection: message.role === 'user' ? 'row' : 'row-reverse',
            }}
          >
            <Avatar
              sx={{
                bgcolor: message.role === 'user' ? '#60A5FA' : '#F97316',
                width: 36,
                height: 36,
                mr: message.role === 'user' ? 1 : 0,
                ml: message.role === 'assistant' ? 1 : 0,
              }}
              src={message.role !== 'user' ? currentAlchemyst.avatarUrl : ''}
            >
              {message.role === 'user' && 'U'}
            </Avatar>

            <Paper
              elevation={1}
              sx={{
                p: 2,
                maxWidth: '70%',
                borderRadius: 2,
                bgcolor:
                  message.role === 'user'
                    ? '#000000'
                    : '#434343',
                borderTopLeftRadius: message.role === 'user' ? 0 : 2,
                borderTopRightRadius: message.role === 'assistant' ? 0 : 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">
                  {message.role === 'user' ? 'User' : currentAlchemyst.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatTimestamp(
                    message.createdAt ?? new Date().toISOString()
                  )}
                </Typography>
              </Box>

              <Typography variant="body2">{message.message}</Typography>
            </Paper>
          </Box>
        ))
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50%',
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No transcript available for this call
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default CallTranscript;
